/**
 * NovaQ Token удирдлагын төв файл
 * NovaQ системийн token-уудыг удирдах
 */

export interface NovaQTokenInfo {
  novaqToken: string | null;
  etaxToken: string | null;
  hasValidToken: boolean;
  primaryToken: string | null;
  tokenType: 'novaq' | 'etax' | 'mixed' | null;
  expiry: number | null;
  isExpired: boolean;
}

export interface NovaQTokenResponse {
  success: boolean;
  token: string;
  refreshToken?: string;
  expiresIn?: number;
  tokenType?: string;
  message?: string;
}

/**
 * localStorage-аас бүх NovaQ token-уудыг авах
 */
export const getNovaQTokenInfo = (): NovaQTokenInfo => {
  const novaqToken = localStorage.getItem('novaqToken');
  const etaxToken = localStorage.getItem('etax_token');
  const tokenExpiry = localStorage.getItem('novaqTokenExpiry');
  
  // Token шалгах - novaqToken эсвэл etax_token байвал зөв
  const hasValidToken = (novaqToken && novaqToken.length > 10) || 
                       (etaxToken && etaxToken.length > 10);
  
  // Эхлээд novaqToken, дараа нь etaxToken
  const primaryToken = novaqToken || etaxToken;
  
  // Token төрөл тодорхойлох
  let tokenType: 'novaq' | null = null;
  if (novaqToken) {
    tokenType = 'novaq';
  }
  
  
  // Expiry time шалгах
  const expiry = tokenExpiry ? parseInt(tokenExpiry) : null;
  const isExpired = expiry ? Date.now() > expiry : false;
  
  return {
    novaqToken,
    etaxToken,
    hasValidToken,
    primaryToken,
    tokenType,
    expiry,
    isExpired
  };
};

/**
 * NovaQ Token-уудыг console-д хэвлэх
 */
export const logNovaQTokenInfo = (tokenInfo: NovaQTokenInfo): void => {
  console.log('🔍 NovaQ Token мэдээлэл:');
  console.log('  - novaqToken:', tokenInfo.novaqToken ? 'Present' : 'Missing');
  console.log('  - etaxToken:', tokenInfo.etaxToken ? 'Present' : 'Missing');
  console.log('  - hasValidToken:', tokenInfo.hasValidToken ? 'Yes' : 'No');
  console.log('  - primaryToken:', tokenInfo.primaryToken ? 'Present' : 'Missing');
  console.log('  - tokenType:', tokenInfo.tokenType);
  console.log('  - expiry:', tokenInfo.expiry ? new Date(tokenInfo.expiry).toISOString() : 'No expiry');
  console.log('  - isExpired:', tokenInfo.isExpired ? 'Yes' : 'No');
};

/**
 * NovaQ Token-уудыг тохируулах - novaqToken-ийг etax_token болгон хуулах
 */
export const setupNovaQTokens = (tokenInfo: NovaQTokenInfo): void => {
  if (tokenInfo.novaqToken) {
    console.log('🔑 Using novaqToken for API calls');
    // novaqToken-г etax_token болгон хуулах (legacy API compatibility)

    localStorage.setItem('novaqToken', tokenInfo.novaqToken);
  } 
};

/**
 * API дуудлагад зориулсан token авах
 */
export const getNovaQApiToken = (): string | null => {
  const tokenInfo = getNovaQTokenInfo();
  return tokenInfo.primaryToken;
};

/**
 * NovaQ Authentication шалгах
 */
export const checkNovaQAuthentication = (): { isAuthenticated: boolean; token: string | null } => {
  const tokenInfo = getNovaQTokenInfo();
  return {
    isAuthenticated: tokenInfo.hasValidToken && !tokenInfo.isExpired,
    token: tokenInfo.primaryToken
  };
};

/**
 * NovaQ Token хадгалах
 */
export const saveNovaQToken = (token: string, expiresIn?: number): void => {
  console.log('💾 NovaQ token хадгалах...');
  
  localStorage.setItem('novaqToken', token);
  
  if (expiresIn) {
    const expiry = Date.now() + (expiresIn * 1000);
    localStorage.setItem('novaqTokenExpiry', expiry.toString());
  }
  
  console.log('✅ NovaQ token амжилттай хадгалагдлаа');
};

/**
 * NovaQ Token цэвэрлэх
 */
export const clearNovaQTokens = (): void => {
  console.log('🗑️ NovaQ tokens цэвэрлэх...');
  
  localStorage.removeItem('novaqToken');
  localStorage.removeItem('novaqTokenExpiry');
  console.log('✅ NovaQ tokens цэвэрлэгдлээ');
};

/**
 * NovaQ Token хүчинтэй эсэхийг шалгах
 */
export const isNovaQTokenValid = (): boolean => {
  const tokenInfo = getNovaQTokenInfo();
  
  if (!tokenInfo.hasValidToken) {
    return false;
  }
  
  if (tokenInfo.isExpired) {
    console.log('⚠️ NovaQ token дууссан');
    return false;
  }
  
  return true;
};

/**
 * NovaQ Token refresh хийх
 */
export const refreshNovaQToken = async (): Promise<boolean> => {
  try {
    console.log('🔄 NovaQ token refresh хийж байна...');
    
    const response = await fetch('/api/auth/refresh', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getNovaQApiToken()}`
      }
    });
    
    if (response.ok) {
      const tokenResponse: NovaQTokenResponse = await response.json();
      if (tokenResponse.success && tokenResponse.token) {
        saveNovaQToken(tokenResponse.token, tokenResponse.expiresIn);
        console.log('✅ NovaQ token амжилттай refresh хийгдлээ');
        return true;
      }
    }
    
    console.error('❌ NovaQ token refresh амжилтгүй:', response.status);
    return false;
  } catch (error) {
    console.error('❌ NovaQ token refresh алдаа:', error);
    return false;
  }
};

/**
 * NovaQ Token авах (автомат refresh хийх)
 */
export const getValidNovaQToken = async (): Promise<string | null> => {
  if (isNovaQTokenValid()) {
    return getNovaQApiToken();
  }
  
  console.log('🔄 NovaQ Token дууссан, refresh хийж байна...');
  const refreshSuccess = await refreshNovaQToken();
  
  if (refreshSuccess) {
    return getNovaQApiToken();
  } else {
    console.error('❌ NovaQ Token refresh амжилтгүй, дахин нэвтэрнэ үү');
    return null;
  }
};

/**
 * NovaQ Token-тай API дуудлага хийх
 */
export const makeNovaQApiCall = async (endpoint: string, options: RequestInit = {}): Promise<Response> => {
  const token = await getValidNovaQToken();
  
  if (!token) {
    throw new Error('NovaQ Token олдсонгүй. Дахин нэвтэрнэ үү.');
  }
  
  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    ...options.headers
  };
  
  return fetch(endpoint, {
    ...options,
    headers
  });
};

/**
 * NovaQ Token-тай холбоотой бүх мэдээллийг авах
 */
export const getNovaQTokenStatus = () => {
  const tokenInfo = getNovaQTokenInfo();
  const authStatus = checkNovaQAuthentication();
  
  return {
    ...tokenInfo,
    ...authStatus,
    timestamp: new Date().toISOString()
  };
};

/**
 * NovaQ Token debug мэдээлэл
 */
export const debugNovaQTokens = (): void => {
  console.log('🔍 NovaQ Token Debug мэдээлэл:');
  console.log('📊 localStorage бүх key-үүд:', Object.keys(localStorage));
  console.log('📊 localStorage token-тай холбоотой key-үүд:', 
    Object.keys(localStorage).filter(key => 
      key.includes('token') || key.includes('Token') || key.includes('novaq') || key.includes('etax')
    )
  );
  
  const tokenInfo = getNovaQTokenInfo();
  logNovaQTokenInfo(tokenInfo);
  
  const authStatus = checkNovaQAuthentication();
  console.log('🔐 Authentication статус:', authStatus);
};

// Legacy function names for backward compatibility
export const getTokenInfo = getNovaQTokenInfo;
export const logTokenInfo = logNovaQTokenInfo;
export const setupTokens = setupNovaQTokens;
export const checkAuthentication = checkNovaQAuthentication;
